import { forwardRef } from 'react';

import { Box, Toast, useToast } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  type RenterInfoRenterAddedDto,
  useRentalManagerRenterContactWebApiHandler_getTenants,
} from '@zg-rentals/rental-manager-env';
import { SECTION_ORDER, TenantSelectFormBody, type TenantSelectFormBodyRef } from '@zg-rentals/modal-tenant';

import { createZgApiHttpClient } from '../../../../../client/zgApiHttpClient';

import { useSelector } from '../../../../selectors/useSelector';
import { RoutingSelectors } from '../../../../selectors';

import { DEACTIVATION_REASONS, type DeactivationReason } from '../constants';

const TOAST_MESSAGE = {
  SUCCESS_ADD_TENANT: 'Tenants added to property dashboard',
} as const;

type AddTenantSelectStepProps = {
  deactivationReason?: DeactivationReason | null;
  onValidationChange?: (isValid: boolean) => void;
  onSubmitSuccess?: () => void;
};

const AddTenantSelectStep = forwardRef<TenantSelectFormBodyRef, AddTenantSelectStepProps>(
  ({ deactivationReason = null, onValidationChange, onSubmitSuccess }, ref) => {
    const queryClient = useQueryClient();
    const { enqueueToast } = useToast();

    const { env, listingAlias } = useSelector((state) => ({
      env: state.env,
      listingAlias: RoutingSelectors.makeListingId(state),
    }));

    const client = createZgApiHttpClient(env.zgApiPublic);

    const prePopulateExistingTenants = deactivationReason === DEACTIVATION_REASONS.TENANT_RENEWED;
    const tenantSelectionTitle =
      deactivationReason === DEACTIVATION_REASONS.FOUND_RENTER_ELSEWHERE
        ? 'Who are your tenants?'
        : 'Who are your current tenants?';

    const tenantSelectionSubtitle =
      deactivationReason === DEACTIVATION_REASONS.FOUND_RENTER_ELSEWHERE
        ? 'Let us know and we’ll guide them on how to pay rent on time, take care of your home, and get settled'
        : '';

    const invalidateGetTenantsQuery = () => {
      queryClient.invalidateQueries({
        queryKey: useRentalManagerRenterContactWebApiHandler_getTenants.getKey({ listingAlias }),
      });
    };

    const handleSubmitSuccess = (_addedRenters?: Array<RenterInfoRenterAddedDto>) => {
      invalidateGetTenantsQuery();
      enqueueToast(<Toast tone="success">{TOAST_MESSAGE.SUCCESS_ADD_TENANT}</Toast>);
      onSubmitSuccess?.();
    };

    return (
      <Box data-testid="AddTenantSelectStep">
        <TenantSelectFormBody
          client={client}
          hideLeaseTermUntilTenantSelected={true}
          includeActiveTenants={true}
          leaseTermsSubtitle="Let us know and we’ll make the digital move-in and payments easier"
          listingAlias={listingAlias}
          prePopulateExistingTenants={prePopulateExistingTenants}
          ref={ref}
          sectionOrder={SECTION_ORDER.TENANTS_FIRST}
          tenantSelectionTitle={tenantSelectionTitle}
          tenantSelectionSubtitle={tenantSelectionSubtitle}
          onSubmitSuccess={handleSubmitSuccess}
          onValidationChange={onValidationChange}
        />
      </Box>
    );
  },
);

AddTenantSelectStep.displayName = 'AddTenantSelectStep';

export default AddTenantSelectStep;
